import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Mail, Globe, MessageCircle, Clock, Shield, ArrowRight, Sparkles, HelpCircle } from 'lucide-react';
import SEO, { SEO_CONFIG } from '@/components/SEO';

const ContactPage = () => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-purple-50 via-white to-purple-50">
      <SEO {...SEO_CONFIG.contact} />
      
      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-r from-purple-600 to-purple-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MessageCircle className="h-16 w-16 mx-auto mb-6 opacity-90" />
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            {isEnglish ? 'Contact Us' : 'Bize Ulaşın'}
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            {isEnglish 
              ? 'We\'re here to help! Reach out to us with any questions, feedback, or partnership inquiries.'
              : 'Size yardımcı olmak için buradayız! Sorularınız, geri bildirimleriniz veya iş birliği teklifleriniz için bizimle iletişime geçin.'}
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Contact Methods */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            {isEnglish ? 'How to Reach Us' : 'Bize Nasıl Ulaşabilirsiniz'}
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Email */}
            <Card className="border-purple-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-purple-100 rounded-xl">
                    <Mail className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-gray-900 mb-1">
                      {isEnglish ? 'Email Support' : 'E-posta Desteği'}
                    </h3>
                    <a 
                      href="mailto:info@pilatika.com" 
                      className="text-purple-600 hover:underline text-lg font-medium"
                    >
                      info@pilatika.com
                    </a>
                    <p className="text-gray-600 text-sm mt-2">
                      {isEnglish 
                        ? 'Best for detailed inquiries and support requests'
                        : 'Detaylı sorular ve destek talepleri için en uygun yöntem'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Website */}
            <Card className="border-pink-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-pink-100 rounded-xl">
                    <Globe className="h-6 w-6 text-pink-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-gray-900 mb-1">
                      {isEnglish ? 'Website' : 'Web Sitesi'}
                    </h3>
                    <a 
                      href="https://pilatika.com" 
                      className="text-pink-600 hover:underline text-lg font-medium"
                    >
                      pilatika.com
                    </a>
                    <p className="text-gray-600 text-sm mt-2">
                      {isEnglish 
                        ? 'Explore our platform and services'
                        : 'Platformumuzu ve hizmetlerimizi keşfedin'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Response Time */}
        <section className="mb-12">
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Clock className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-lg text-gray-900 mb-2">
                    {isEnglish ? 'Response Times' : 'Yanıt Süreleri'}
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    {isEnglish 
                      ? 'We typically respond to all inquiries within 24-48 hours during business days (Monday to Friday). For urgent matters, please include "URGENT" in your email subject line.'
                      : 'Tüm sorularınıza iş günlerinde (Pazartesi-Cuma) genellikle 24-48 saat içinde yanıt veriyoruz. Acil konular için lütfen e-posta konu satırına "ACİL" yazın.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* FAQ Teaser */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            {isEnglish ? 'Frequently Asked Questions' : 'Sık Sorulan Sorular'}
          </h2>
          
          <div className="space-y-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <HelpCircle className="h-5 w-5 text-purple-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {isEnglish ? 'How do I cancel my subscription?' : 'Üyeliğimi nasıl iptal ederim?'}
                    </h3>
                    <p className="text-gray-600 text-sm mt-1">
                      {isEnglish 
                        ? 'You can manage your subscription from your profile settings or contact us for assistance.'
                        : 'Üyeliğinizi profil ayarlarından yönetebilir veya yardım için bizimle iletişime geçebilirsiniz.'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <HelpCircle className="h-5 w-5 text-purple-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {isEnglish ? 'How can I become an instructor?' : 'Nasıl eğitmen olabilirim?'}
                    </h3>
                    <p className="text-gray-600 text-sm mt-1">
                      {isEnglish 
                        ? 'Visit our Become Instructor page to learn about requirements and apply.'
                        : 'Gereksinimler hakkında bilgi almak ve başvurmak için Eğitmen Ol sayfamızı ziyaret edin.'}
                    </p>
                    <Link to="/become-instructor" className="text-purple-600 hover:underline text-sm inline-flex items-center mt-2">
                      {isEnglish ? 'Learn more' : 'Daha fazla bilgi'}
                      <ArrowRight className="h-3 w-3 ml-1" />
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <HelpCircle className="h-5 w-5 text-purple-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {isEnglish ? 'Is my payment information secure?' : 'Ödeme bilgilerim güvende mi?'}
                    </h3>
                    <p className="text-gray-600 text-sm mt-1">
                      {isEnglish 
                        ? 'Yes, all payments are processed securely through iyzico, a trusted payment provider.'
                        : 'Evet, tüm ödemeler güvenilir ödeme sağlayıcısı iyzico üzerinden güvenli bir şekilde işlenir.'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Payment Security */}
        <section className="mb-12">
          <Card className="border-green-200 bg-gradient-to-br from-green-50 to-white">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <Shield className="h-10 w-10 text-green-600" />
                  <div>
                    <h2 className="font-semibold text-lg text-gray-900">
                      {isEnglish ? 'Secure Payments' : 'Güvenli Ödemeler'}
                    </h2>
                    <p className="text-gray-600 text-sm">
                      {isEnglish 
                        ? 'All transactions are protected with bank-level security'
                        : 'Tüm işlemler banka seviyesinde güvenlikle korunmaktadır'}
                    </p>
                  </div>
                </div>
                <img 
                  src="/iyzico-logo-pack/footer_iyzico_ile_ode/Colored/logo_band_colored@2x.png" 
                  alt="iyzico ile güvenli ödeme" 
                  className="h-8"
                  width="200"
                  height="32"
                  loading="lazy"
                />
              </div>
            </CardContent>
          </Card>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-gradient-to-r from-purple-600 to-purple-800 rounded-2xl p-8 text-white">
          <h2 className="text-2xl font-bold mb-4">
            {isEnglish ? 'Need Instant Help?' : 'Anında Yardıma mı İhtiyacınız Var?'}
          </h2>
          <p className="text-white/90 mb-6 max-w-xl mx-auto">
            {isEnglish 
              ? 'Try our AI Coach for instant answers to wellness questions, available 24/7.'
              : 'Wellness sorularınız için 7/24 erişilebilir AI Koçumuzu deneyin.'}
          </p>
          <Link to="/ai-coach">
            <Button size="lg" className="bg-white text-purple-700 hover:bg-gray-100">
              <Sparkles className="mr-2 h-4 w-4" />
              {isEnglish ? 'Chat with AI Coach' : 'AI Koç ile Konuş'}
            </Button>
          </Link>
        </section>

      </div>
    </div>
  );
};

export default ContactPage;
